<template>
    <div class="customer-service">
        <!-- 聊天弹框 -->
        <transition name="chat-slide">
            <section v-if="visible" class="chat-window">
                <!-- 头部 -->
                <header class="chat-header">
                    <div class="service-info">
                        <div class="service-avatar">M</div>

                        <div>
                            <div class="service-name">Asesor de moda</div>
                            <div class="service-status">
                                <span class="status-dot"></span>
                                En línea
                            </div>
                        </div>
                    </div>

                    <button type="button" class="close-button" aria-label="Cerrar chat" @click="closeChat">
                        <i class="el-icon-close"></i>
                    </button>
                </header>

                <!-- 消息区域 -->
                <main ref="messageList" class="message-list">
                    <div v-for="message in messages" :key="message.id" class="message-row" :class="{
                        'message-row--user': message.role === 'user',
                        'message-row--service': message.role === 'service'
                    }">
                        <div v-if="message.role === 'service'" class="message-avatar">
                            M
                        </div>

                        <div class="message-content">
                            <div class="message-bubble">
                                {{ message.content }}
                            </div>

                            <div class="message-time">
                                {{ message.time }}
                            </div>
                        </div>
                    </div>

                    <!-- 自动回复加载动画 -->
                    <div v-if="replying" class="message-row message-row--service">
                        <div class="message-avatar">M</div>

                        <div class="typing-bubble">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                </main>

                <!-- 快捷问题 -->
                <div class="quick-questions">
                    <button v-for="question in quickQuestions" :key="question" type="button" class="quick-question"
                        @click="sendQuickQuestion(question)">
                        {{ question }}
                    </button>
                </div>

                <!-- 输入区域 -->
                <footer class="chat-footer">
                    <el-input v-model="inputValue" type="textarea" :rows="2" resize="none" maxlength="300"
                        placeholder="Pregunta sobre tallas, stock, envíos o devoluciones..."
                        @keydown.enter.native="handleEnter"></el-input>

                    <el-button type="primary" class="send-button" :disabled="!inputValue.trim() || replying"
                        @click="sendMessage">
                        Enviar
                    </el-button>
                </footer>
            </section>
        </transition>

        <!-- 右侧悬浮按钮 -->
        <button type="button" class="service-button" aria-label="Abrir chat de atención" @click="toggleChat">
            <i :class="visible ? 'el-icon-close' : 'el-icon-service'"></i>

            <span v-if="unreadCount && !visible" class="unread-count">
                {{ unreadCount > 99 ? "99+" : unreadCount }}
            </span>
        </button>
    </div>
</template>

<script>
export default {
    name: "CustomerService",

    data() {
        return {
            visible: false,
            inputValue: "",
            replying: false,
            unreadCount: 0,
            replyTimer: null,

            quickQuestions: [
                "¿Cómo elijo mi talla?",
                "¿Cuándo se envía?",
                "Cambios y devoluciones",
                "Recomiéndame un look"
            ],

            messages: [
                {
                    id: 1,
                    role: "service",
                    content: "¡Hola! Te damos la bienvenida a nuestra tienda. Soy tu asesor de moda y puedo ayudarte con tallas, stock, envíos, devoluciones y recomendaciones de estilo.",
                    time: this.getCurrentTime()
                }
            ]
        };
    },

    beforeDestroy() {
        if (this.replyTimer) {
            clearTimeout(this.replyTimer);
        }
    },

    methods: {
        toggleChat() {
            this.visible = !this.visible;

            if (this.visible) {
                this.unreadCount = 0;
                this.scrollToBottom();
            }
        },

        closeChat() {
            this.visible = false;
        },

        handleEnter(event) {
            // Shift + Enter 换行
            if (event.shiftKey) {
                return;
            }

            event.preventDefault();
            this.sendMessage();
        },

        sendQuickQuestion(question) {
            if (this.replying) {
                return;
            }

            this.inputValue = question;
            this.sendMessage();
        },

        sendMessage() {
            const content = this.inputValue.trim();

            if (!content || this.replying) {
                return;
            }

            this.messages.push({
                id: Date.now(),
                role: "user",
                content: content,
                time: this.getCurrentTime()
            });

            this.inputValue = "";
            this.replying = true;
            this.scrollToBottom();

            // 模拟客服自动回复延迟
            this.replyTimer = setTimeout(() => {
                this.messages.push({
                    id: Date.now() + 1,
                    role: "service",
                    content: this.getAutoReply(content),
                    time: this.getCurrentTime()
                });

                this.replying = false;

                if (!this.visible) {
                    this.unreadCount += 1;
                }

                this.scrollToBottom();
            }, 1000);
        },

        getAutoReply(content) {
            const text = content
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");

            const includesAny = keywords => {
                return keywords.some(keyword => text.includes(keyword));
            };

            if (includesAny(["persona", "humano", "agente", "asesor", "atencion al cliente"])) {
                return "Actualmente soy el asistente virtual. Si necesitas ayuda de una persona, deja tu consulta y tus datos de contacto para que nuestro equipo pueda responderte lo antes posible.";
            }

            if (includesAny(["talla", "tamano", "medida", "grande", "pequeno", "talla s", "talla m", "talla l", "xl"])) {
                return "El ajuste puede variar según el modelo. Indícame el producto, tu altura, tu peso y la talla que usas normalmente para orientarte. Consulta también la guía de tallas de la página del producto.";
            }

            if (includesAny(["stock", "existencia", "disponible", "agotado", "reposicion", "reponer"])) {
                return "Los colores y las tallas que puedes seleccionar en la página del producto corresponden normalmente al stock disponible. Si aparece agotado, vuelve a consultar la página para conocer futuras reposiciones.";
            }

            if (includesAny(["envio", "enviar", "entrega", "mensajeria", "paquete", "cuando llega", "cuanto tarda"])) {
                return "Prepararemos tu pedido lo antes posible después de confirmar el pago. Consulta el plazo indicado en el producto y el seguimiento del pedido para conocer la fecha estimada de entrega.";
            }

            if (includesAny(["devolver", "devolucion", "cambio", "reembolso", "no me queda", "no me gusta"])) {
                return "Para solicitar un cambio o una devolución, comprueba que la prenda cumple nuestras condiciones y envía la solicitud desde tu pedido. Las prendas usadas, lavadas, sin etiquetas o que no sean aptas para una segunda venta pueden quedar excluidas. Consulta la política aplicable al producto.";
            }

            if (includesAny(["pedido", "numero de pedido", "cancelar", "direccion", "seguimiento"])) {
                return "Puedes consultar el estado en “Mis pedidos”. Si necesitas cancelar el pedido o cambiar la dirección, contacta con nuestro equipo cuanto antes; normalmente no es posible modificarlo una vez enviado.";
            }

            if (includesAny(["color", "diferencia de color", "foto", "imagen", "tono"])) {
                return "Intentamos mostrar los colores con la mayor fidelidad posible. Sin embargo, la iluminación de las fotografías y la configuración de cada pantalla pueden producir pequeñas variaciones de tono.";
            }

            if (includesAny(["lavar", "lavado", "cuidado", "destiñe", "encoge", "planchar"])) {
                return "Revisa la etiqueta de cuidado de la prenda y sigue sus indicaciones. Te recomendamos lavar por separado los colores claros y oscuros y evitar remojar la prenda durante mucho tiempo.";
            }

            if (includesAny(["combinar", "look", "estilo", "recomienda", "recomendacion", "que me pongo", "outfit"])) {
                return "¡Claro! Cuéntame tu altura, el estilo que te gusta y la ocasión —por ejemplo, trabajo, una cita o un look informal— y te ayudaré a crear una combinación.";
            }

            if (includesAny(["oferta", "descuento", "cupon", "promocion", "rebaja"])) {
                return "Las promociones vigentes aparecen en la página del producto y al finalizar la compra. Antes de pagar, comprueba si tienes algún cupón o descuento disponible.";
            }

            if (includesAny(["precio", "cuanto cuesta", "costo", "importe"])) {
                return "El precio actualizado aparece en la página del producto. El importe final, incluidos los descuentos disponibles, se mostrará antes de confirmar el pago.";
            }

            if (includesAny(["pago", "pagar", "tarjeta", "metodo de pago"])) {
                return "Los métodos de pago disponibles se mostrarán al finalizar la compra. Selecciona el que prefieras antes de confirmar el pedido.";
            }

            if (includesAny(["contacto", "telefono", "correo", "email", "horario"])) {
                return "Puedes comunicarte con nosotros mediante los datos de contacto del pie de página. Incluye tu número de pedido y una descripción del problema para que podamos ayudarte más rápido.";
            }

            if (includesAny(["hola", "buenos dias", "buenas tardes", "buenas noches", "hello", "estas ahi"])) {
                return "¡Hola! Estoy aquí para ayudarte. Puedes preguntarme sobre tallas, stock, envíos, devoluciones o recomendaciones de estilo.";
            }

            return "He recibido tu consulta. Añade el nombre del producto, el color, la talla o el número de pedido para que pueda ayudarte mejor. Para casos especiales, también puedes solicitar atención personal.";
        },

        getCurrentTime() {
            const date = new Date();

            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");

            return `${hours}:${minutes}`;
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const messageList = this.$refs.messageList;

                if (messageList) {
                    messageList.scrollTop = messageList.scrollHeight;
                }
            });
        }
    }
};
</script>

<style scoped>
.customer-service {
    position: relative;
    z-index: 3000;
}

/* 右侧悬浮按钮 */
.service-button {
    position: fixed;
    right: 28px;
    bottom: 36px;
    z-index: 3001;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 58px;
    height: 58px;
    padding: 0;

    border: 1px solid #333333;
    border-radius: 50%;
    outline: none;

    background: #1a1a1a;
    color: #ffffff;
    box-shadow: 0 10px 30px rgba(26, 26, 26, 0.35);

    cursor: pointer;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.service-button:hover {
    transform: translateY(-3px);
    background: #333333;
    box-shadow: 0 14px 34px rgba(26, 26, 26, 0.45);
}

.service-button i {
    font-size: 26px;
}

.unread-count {
    position: absolute;
    top: -4px;
    right: -4px;

    min-width: 18px;
    height: 18px;
    padding: 0 4px;

    border: 2px solid #ffffff;
    border-radius: 10px;

    background: #f56c6c;
    color: #ffffff;

    font-size: 11px;
    line-height: 18px;
    text-align: center;
}

/* 聊天弹框 */
.chat-window {
    position: fixed;
    right: 28px;
    bottom: 108px;
    z-index: 3000;

    display: flex;
    flex-direction: column;

    width: 370px;
    height: 540px;
    overflow: hidden;

    border: 1px solid #d9d9d9;
    border-radius: 16px;

    background: #ffffff;
    box-shadow: 0 18px 60px rgba(0, 0, 0, 0.24);
}

/* 头部 */
.chat-header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;

    min-height: 72px;
    padding: 0 18px;

    border-bottom: 1px solid #303030;
    background: #1a1a1a;
    color: #ffffff;
}

.service-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.service-avatar,
.message-avatar {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;

    border-radius: 50%;

    background: #ffffff;
    color: #1a1a1a;

    font-weight: 600;
}

.service-avatar {
    width: 42px;
    height: 42px;
}

.service-name {
    margin-bottom: 5px;
    font-size: 16px;
    font-weight: 600;
}

.service-status {
    display: flex;
    align-items: center;
    gap: 5px;

    font-size: 12px;
    opacity: 0.9;
}

.status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #62e693;
}

.close-button {
    padding: 6px;

    border: 0;
    outline: none;

    background: transparent;
    color: #ffffff;

    font-size: 19px;
    cursor: pointer;
}

/* 消息区域 */
.message-list {
    flex: 1;
    min-height: 0;
    padding: 18px;
    overflow-y: auto;

    background: #f3f3f1;
}

.message-row {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin-bottom: 18px;
}

.message-row--user {
    justify-content: flex-end;
}

.message-avatar {
    width: 32px;
    height: 32px;
    border: 1px solid #1a1a1a;
    background: #1a1a1a;
    color: #ffffff;
    font-size: 13px;
}

.message-content {
    max-width: 76%;
}

.message-row--user .message-content {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.message-bubble {
    padding: 10px 13px;

    border: 1px solid #e6e6e6;
    border-radius: 4px 14px 14px 14px;

    background: #ffffff;
    color: #242424;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.05);

    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
}

.message-row--user .message-bubble {
    border-radius: 14px 4px 14px 14px;
    border-color: #1a1a1a;
    background: #1a1a1a;
    color: #ffffff;
}

.message-time {
    margin-top: 5px;
    color: #8d8d8d;
    font-size: 11px;
}

/* 正在输入 */
.typing-bubble {
    display: flex;
    align-items: center;
    gap: 4px;

    padding: 13px 15px;
    border: 1px solid #e6e6e6;
    border-radius: 4px 14px 14px 14px;

    background: #ffffff;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.05);
}

.typing-bubble span {
    width: 6px;
    height: 6px;

    border-radius: 50%;
    background: #777777;

    animation: typing 1.2s infinite ease-in-out;
}

.typing-bubble span:nth-child(2) {
    animation-delay: 0.15s;
}

.typing-bubble span:nth-child(3) {
    animation-delay: 0.3s;
}

/* 快捷问题 */
.quick-questions {
    display: flex;
    flex-shrink: 0;
    gap: 7px;

    padding: 10px 14px;
    overflow-x: auto;

    border-top: 1px solid #e5e5e5;
    background: #ffffff;
}

.quick-question {
    flex-shrink: 0;
    padding: 6px 10px;

    border: 1px solid #d8d8d8;
    border-radius: 14px;
    outline: none;

    background: #f5f5f5;
    color: #1a1a1a;

    font-size: 12px;
    cursor: pointer;
}

.quick-question:hover {
    border-color: #1a1a1a;
    background: #eeeeee;
}

/* 底部输入框 */
.chat-footer {
    display: flex;
    flex-shrink: 0;
    align-items: flex-end;
    gap: 10px;

    padding: 13px 14px;

    border-top: 1px solid #333333;
    background: #1a1a1a;
}

.chat-footer .el-textarea {
    flex: 1;
}

.chat-footer ::v-deep .el-textarea__inner {
    min-height: 56px !important;
    padding: 8px 10px;

    border-color: #444444;
    border-radius: 9px;

    background: #292929;
    color: #ffffff;
    font-family: inherit;
    line-height: 20px;
}

.chat-footer ::v-deep .el-textarea__inner::placeholder {
    color: #969696;
}

.chat-footer ::v-deep .el-textarea__inner:focus {
    border-color: #ffffff;
}

.send-button {
    height: 36px;
    padding: 0 16px;
    border-radius: 8px;
}

.send-button.el-button--primary {
    border-color: #ffffff;
    background: #ffffff;
    color: #1a1a1a;
    font-weight: 600;
}

.send-button.el-button--primary:hover,
.send-button.el-button--primary:focus {
    border-color: #e8e8e8;
    background: #e8e8e8;
    color: #1a1a1a;
}

.send-button.el-button--primary.is-disabled {
    border-color: #4a4a4a;
    background: #4a4a4a;
    color: #999999;
}

.message-list::-webkit-scrollbar {
    width: 5px;
}

.message-list::-webkit-scrollbar-thumb {
    border-radius: 5px;
    background: #a8a8a8;
}

.message-list::-webkit-scrollbar-track {
    background: transparent;
}

/* 打开关闭动画 */
.chat-slide-enter-active,
.chat-slide-leave-active {
    transition:
        opacity 0.22s ease,
        transform 0.22s ease;
}

.chat-slide-enter,
.chat-slide-leave-to {
    opacity: 0;
    transform: translateY(18px) scale(0.96);
}

@keyframes typing {

    0%,
    60%,
    100% {
        transform: translateY(0);
        opacity: 0.45;
    }

    30% {
        transform: translateY(-4px);
        opacity: 1;
    }
}

/* 手机端 */
@media screen and (max-width: 600px) {
    .service-button {
        right: 16px;
        bottom: 20px;
        width: 52px;
        height: 52px;
    }

    .chat-window {
        right: 12px;
        bottom: 82px;

        width: calc(100vw - 24px);
        height: min(560px, calc(100vh - 110px));

        border-radius: 14px;
    }
}
</style>